<template>
  <div class="page-container">
    <div class="import-export-title primary-color import-export-title-margins">
      <v-icon color="secondary" class="arrow-back" @click="goBack()"> arrow_back</v-icon>
      <span style="margin-left: 16px">{{ adminText('organizationAll.import_subscribers_e8c4bc08', 'Import Subscribers') }}</span>
    </div>
    <div class="import-export-container import-export-container-margins">
      <module-title
        color="#0277BD"
        class="import-export-module-title-margins"
        :title="adminText('organizationAll.1_create_the_csv_file_78f65679', '1. Create the CSV file')"
      />
      <div class="import-export-description-margins">
        {{ adminText('organizationAll.first_you_need_to_download_the_official_csv_t_58bc94b3', 'First, you need to download the official CSV template file which contains all of the attendee fields you can fill out. Make sure you enter all of the required fields for all of your attendees to make it valid.') }}
      </div>
      <div class="import-export-description-margins">
        {{ adminText('organizationAll.your_template_contains_following_fields_8cc62c32', 'Your template contains following fields:') }}<br>
        <span v-for="fieldName in orderedFieldNames" :key="fieldName" class="import-field-list">
          {{ `• ${fieldName}` }}
          <template v-if="['First name', 'Last name', 'Email'].includes(fieldName)">
            {{ adminText('organizationAll.required_0002fd23', '(required)') }}
          </template>
          <br>
        </span>
      </div>
      <div class="import-export-buttons">
        <a
          :href="`${serverURL}/files/sport-organizations/${organizationSlug}/memberships/export/template/csv/${token}`"
        >
          <main-button depressed :text="adminText('organizationAll.download_template_d2685655', 'Download template')" />
        </a>
      </div>
    </div>
    <div class="import-export-container import-export-container-margins">
      <module-title
        color="#0277BD"
        class="import-export-module-title-margins"
        :title="adminText('organizationAll.2_upload_the_csv_file_d3cf8947', '2. Upload the CSV file')"
      />
      <div class="import-export-description-margins">
        {{ adminText('organizationAll.after_you_finish_adding_the_attendees_data_yo_f76061cb', 'After you finish adding the attendees data, you can upload the file here. Don\'t worry, if something goes wrong, we will let you know.') }}
      </div>
      <div class="import-export-buttons">
        <main-button depressed :text="adminText('organizationAll.upload_file_503a3d8c', 'Upload file')" @click="openFSPicker" />
      </div>
      <hidden-file-input
        ref="file-input"
        accept="text/csv .csv"
        max-file-size-mb="10"
        @file="processFile($event)"
      />
    </div>
    <success-snackbar
      :open="importStatus === ImportStatusType.SUCCESS"
      :title="adminText('organizationAll.success_42a8f651', 'Success')"
      :message="adminText('organizationAll.all_memberships_were_successfully_imported_a51f0b70', 'All memberships were successfully imported.')"
    />
    <errors-snackbar
      :open="importStatus === ImportStatusType.ERROR"
      :errors="importErrors"
      :title="adminText('organizationAll.error_7f2f6a15', 'Error')"
      :message="adminText('organizationAll.we_encountered_errors_during_validation_proce_379150dc', 'We encountered errors during validation process.')"
    />
    <progress-dialog
      :open="importStatus === ImportStatusType.PROGRESS"
      :message="adminText('organizationAll.importing_memberships_cfc49176', 'Importing memberships...')"
    />
  </div>
</template>
<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import ErrorsSnackbar from '@/components/shared/snackbars/errors-snackbar.vue'
import SuccessSnackbar from '@/components/shared/snackbars/success-snackbar.vue'
import ModuleTitle from '@/components/shared/module-title.vue'
import ProgressDialog from '@/components/shared/dialogs/progress-dialog.vue'
import HiddenFileInput from '@/components/shared/hidden-file-input.vue'
import { serverURL } from '@/utils'
import { getSubscribersExportToken, uploadSportMembershipsCSV } from '@/http'
import { GET_ORGANIZATION_BY_SLUG } from '@/graphql/organization.graphql'

const ImportStatusType = Object.freeze({
  ERROR: 'error',
  SUCCESS: 'success',
  PROGRESS: 'progress',
})

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-subscriptions-import',
  components: { HiddenFileInput, ProgressDialog, ModuleTitle, SuccessSnackbar, ErrorsSnackbar },
  props: {
    organizationSlug: String,
    membershipPlanId: String,
  },
  data() {
    return {
      serverURL,
      token: undefined,
      importStatus: undefined,
      importErrors: [],
      ImportStatusType,
      fields: {},
    }
  },
  computed: {
    orderedFieldNames() {
      return Object.keys(this.fields).sort((fieldA, fieldB) => {
        const orderA =
          this.fields[fieldA] && this.fields[fieldA].order !== undefined
            ? this.fields[fieldA].order
            : Number.MAX_SAFE_INTEGER
        const orderB =
          this.fields[fieldB] && this.fields[fieldB].order !== undefined
            ? this.fields[fieldB].order
            : Number.MAX_SAFE_INTEGER

        return orderA - orderB
      })
    },
  },
  async created() {
    const { data } = await getSubscribersExportToken(this.organizationSlug)
    if (!data) {
      // handle
    }

    this.token = data.token
  },
  async mounted() {
    await this.getOrganization()
  },
  methods: {
    async processFile(file) {
      this.importStatus = ImportStatusType.PROGRESS
      const {
        data: { errors },
      } = await uploadSportMembershipsCSV(
        this.organizationSlug,
        file,
        this.token,
        this.membershipPlanId,
      )
      if (errors) {
        this.importErrors = errors
        this.importStatus = ImportStatusType.ERROR
      } else {
        this.importStatus = ImportStatusType.SUCCESS
      }
    },
    async getOrganization() {
      const { data } = await this.$apollo.query({
        query: GET_ORGANIZATION_BY_SLUG,
        variables: { organizationSlug: this.organizationSlug },
      })

      this.fields = data.getOrganizationBySlug.attendeeFields.membershipFields || {}
    },
  },
}
</script>

<style scoped>
.arrow-back {
  cursor: pointer;
  margin-bottom: 2px;
}

.import-export-container {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding-top: 1.5rem;
  max-width: 676px !important;
}

.import-export-title-margins {
  margin-top: 0;
}

.import-export-module-title-margins {
  margin-bottom: 0.75rem;
}

.import-export-description-margins {
  text-align: left;
  margin-right: 2.25rem;
  margin-bottom: 1.5rem;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #666666;
}

.import-export-buttons {
  margin-bottom: 32px;
}

@media (max-width: 800px) {
  .import-export-title-margins,
  .import-export-title,
  .import-export-description-margins,
  .import-export-buttons,
  .import-export-module-title-margins {
    margin-left: 0;
    margin-right: 0;
  }

  .import-export-container {
    padding: 1.5rem 24px 0;
  }
}

.import-export-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}

.import-export-container-margins {
  padding-left: 2.25rem;
}

.page-container {
  display: flex;
  row-gap: 1.5rem;
  flex-direction: column;
}

.import-field-list {
  padding-left: 0.625rem;
}

.import-export-page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}
</style>
